import axios from "./axios";
import { MediaAssetQueryType } from "../components/common/QueryTypes";

/**
 * Which package an export builds (AKL-810, AKL-1026), spelled exactly as the API spells
 * it. `camtrap_dp` is the Camtrap DP 1.0.2 data package; `wildlife_insights` is Wildlife
 * Insights' four bulk-upload CSVs.
 */
export type ExportFormat = "camtrap_dp" | "wildlife_insights";

/**
 * Request body for POST /exports. `filters` is the *same* filter object the
 * review feed posts to /media-assets/query, so the working set a user curated
 * in the UI is exactly what they take away.
 */
export type ExportRequestType = {
  project_id: number;
  filters: MediaAssetQueryType["filters"];
  // No `include_media_files`: an export is always the data package. Photo bytes never pass
  // through the API, so fetching them is a separate client-side act against a finished
  // export — see fetchExportMediaUrls below.
  include_ai_predictions?: boolean;
  include_flat_csv?: boolean;
  include_megadetector_json?: boolean;
  include_drafts?: boolean;
  skip_incomplete_deployments?: boolean;
  assume_timezone?: string | null;
  /** Omitted means Camtrap DP, the server's default. */
  format?: ExportFormat;
  /** Wildlife Insights only: prepended to every `images.location`. Validated server-side. */
  image_location_prefix?: string;
};

export type ExportEstimateDto = {
  media_count: number;
  sized_count: number;
  known_bytes: number;
  /** Null when no media has a recorded size yet — show the count alone, not a guess. */
  estimated_bytes: number | null;
  /** True when some media had no size to measure, so the figure is approximate. */
  is_estimate: boolean;
  /**
   * Whether the photos are small enough to zip in the browser, or the user needs the
   * desktop transfer tool. Not a limit on the export itself: every export builds, and
   * every export's photos are retrievable one way or the other. Unknown size counts as
   * too large — promising an in-browser download we cannot size is the worse failure.
   */
  browser_download_supported: boolean;
  browser_max_bytes: number;
  browser_max_files: number;
};

/**
 * Size up an export before committing to it: how many images the current filters match
 * and roughly how large they are. Runs the same filter resolution the real export uses.
 *
 * Bypasses the global error interceptor — this fires whenever the panel opens, and a
 * failed estimate should quietly leave the figure blank rather than raise a toast for
 * something the user did not explicitly ask for.
 * @param request the same body POST /exports takes
 * @returns the estimate
 */
export async function estimateExport(
  request: ExportRequestType,
): Promise<ExportEstimateDto> {
  try {
    const resp = await axios.post("/exports/estimate", request, {
      skipGlobalErrorHandler: true,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export type ExportStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "expired";

export type ExportJobDto = {
  guid: string;
  project_id: number;
  status: ExportStatus;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  deployment_count: number | null;
  media_count: number | null;
  observation_count: number | null;
  excluded_human_media_count: number | null;
  result_size_bytes: number | null;
  warnings: string[];
  error: string | null;
  /**
   * The format the job was built in. A plain string, not `ExportFormat`: it is read back
   * from the server, so normalise it with `parseExportFormat` before use.
   */
  format?: string;
};

export type ExportListDto = {
  exports: ExportJobDto[];
};

export type ExportDownloadDto = {
  download_url: string;
  expires_at: string | null;
};

/**
 * Queue an export for a project in the requested format — a Camtrap DP data package
 * (AKL-554) unless `format` asks for Wildlife Insights bulk upload (AKL-1026).
 * Returns immediately with a job whose status starts at "queued"; the package
 * is built server-side afterwards, so the caller must poll `getExport`.
 * @param request the export request (project, filters and output options)
 * @returns the created export job
 */
export async function createExport(
  request: ExportRequestType,
): Promise<ExportJobDto> {
  try {
    const resp = await axios.post("/exports", request);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Fetch the current state of one export job.
 *
 * Bypasses the global error interceptor (`skipGlobalErrorHandler`) because this is
 * polled every couple of seconds while an export runs: a transient failure is retried
 * silently by the caller, and a toast on every attempt would be noise. Callers are
 * responsible for surfacing a persistent failure themselves. The other export calls
 * keep the global handler, since those are user-initiated and a toast is the right
 * feedback there.
 * @param guid the export job guid
 * @returns the export job
 */
export async function getExport(
  guid: string,
  options: { bypassGlobalErrorHandler?: boolean } = {},
): Promise<ExportJobDto> {
  const { bypassGlobalErrorHandler = true } = options;
  try {
    const resp = await axios.get(`/exports/${guid}`, {
      skipGlobalErrorHandler: bypassGlobalErrorHandler,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/** HTTP status from a rejected request, or undefined when there was no response. */
const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

/**
 * True for an authentication failure — a 401, the session no longer valid.
 *
 * These must NOT be retried, and must not be reported as connectivity problems: the
 * caller should re-issue once through the global handler so the app's standard
 * session-expiry behaviour still applies. A 403 is deliberately not one of these
 * (AKL-939): it is the server refusing this job to a live session, a definitive answer
 * like any other 4xx, so it is left to `isRetryablePollFailure`'s callers rather than
 * shown as an expired session.
 * @param err the rejected error
 * @returns whether the request failed because the session is no longer valid
 */
export const isAuthFailure = (err: unknown): boolean => statusOf(err) === 401;

/**
 * True when a failed status poll is worth retrying — a lost connection (no response at
 * all) or a server-side 5xx. A 4xx is a definitive answer and retrying it just delays
 * telling the user.
 * @param err the rejected error
 * @returns whether the caller should retry
 */
export const isRetryablePollFailure = (err: unknown): boolean => {
  const status = statusOf(err);
  return status === undefined || status >= 500;
};

/**
 * Get a time-limited download URL for a completed export.
 * The archive lives in blob storage rather than being streamed through the API,
 * so this returns a signed link the browser fetches directly.
 * @param guid the export job guid
 * @returns the download URL and its expiry
 */
export async function getExportDownloadUrl(
  guid: string,
): Promise<ExportDownloadDto> {
  try {
    const resp = await axios.get(`/exports/${guid}/download`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/** One photo to fetch, and where it belongs inside the package. */
export type ExportMediaUrlDto = {
  media_guid: string;
  /**
   * Byte-identical to this media's `filePath` in media.csv. Write the file here — never
   * derive a path from the URL, because the storage layout and the CamTrap DP layout are
   * different things.
   */
  relative_path: string;
  url: string;
  size_bytes: number | null;
  /** Hex MD5 where known; null for media the EXIF worker has not reached yet. */
  content_md5_hex: string | null;
};

export type ExportMediaUrlsDto = {
  items: ExportMediaUrlDto[];
  /** Null on the last page. */
  next_cursor: string | null;
  expires_at: string | null;
};

/**
 * Fetch one page of signed URLs for an export's photos.
 *
 * The bytes come straight from blob storage to the browser — they never pass through the
 * API. Paged rather than all-at-once so a URL only has to outlive one batch, and so the
 * server re-checks permission as a download proceeds.
 *
 * A page re-fetched to replace expired URLs mid-download (AKL-892) passes
 * `suppressUnexpectedErrorRedirect`: a 5xx there must reject so the caller can record
 * one image failed and carry on, not hard-redirect the page and discard everything
 * already fetched. The initial walk keeps the default handling.
 * @param guid the export job guid
 * @param cursor the `next_cursor` from the previous page, or undefined to start
 * @param options `suppressUnexpectedErrorRedirect` to get a rejection instead of the
 *   redirect on a 5xx
 * @returns one page of media URLs
 */
export async function fetchExportMediaUrls(
  guid: string,
  cursor?: string,
  options: { suppressUnexpectedErrorRedirect?: boolean } = {},
): Promise<ExportMediaUrlsDto> {
  try {
    const resp = await axios.get(`/exports/${guid}/media-urls`, {
      params: cursor ? { cursor } : undefined,
      suppressUnexpectedErrorRedirect:
        options.suppressUnexpectedErrorRedirect === true,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * List recent exports for a project, newest first.
 *
 * Bypasses the global error interceptor for the same reason `estimateExport` does: the
 * export panel asks for this on every open, without the user requesting it, and a
 * failure should quietly leave the list out rather than raise a toast or a redirect
 * (AKL-892). Callers fall back to showing nothing.
 * @param projectId the project id
 * @param limit maximum number of jobs to return
 * @returns the export jobs
 */
export async function listExports(
  projectId: number,
  limit = 25,
): Promise<ExportListDto> {
  try {
    const resp = await axios.get(`/exports/project/${projectId}`, {
      params: { limit },
      skipGlobalErrorHandler: true,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}
